import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BillingHistoryEvent, BillingStatus } from '../types';
import { apiClient } from '../services/apiClient';
import { authService } from '../services/authService';
import { useNotification } from '../hooks/useNotification';
import { useConfirm } from '../hooks/useConfirm';
import { useReport } from '../hooks/useReport';
import { useReportBilling } from '../hooks/useReportBilling';
import { ReportInfo, ReportStatusInfo } from '../components/sidebar';
import { FormHeader } from '../components/FormHeader';
import { BreadcrumbItem } from '../components/Breadcrumb';
import { formatMinutesToHoursMinutes, formatMinutesToWorkdayFormat, parseWorkdayFormatToMinutes } from '../utils/timeUtils';
import { safeFormatDate } from '../utils/dateUtils';
import { getDashboardPath } from '../utils/navigationUtils';
import './ReportBillingPage.css';

/** SPEC 5/7: transizioni ammesse, stesso pattern di validStatusTransitions in ReportForm.tsx. */
const validBillingStatusTransitions = (status: string | null): BillingStatus[] => {
  switch (status) {
    case BillingStatus.TO_VALIDATE:
      return [BillingStatus.TO_VALIDATE, BillingStatus.VALIDATED];
    case BillingStatus.VALIDATED:
      return [BillingStatus.VALIDATED, BillingStatus.TO_VALIDATE, BillingStatus.INVOICED];
    case BillingStatus.INVOICED:
      return [BillingStatus.INVOICED];
    default:
      return [];
  }
};

const getBillingStatusColorVar = (status: string | null | undefined): string => {
  switch (status) {
    case BillingStatus.TO_VALIDATE:
      return 'var(--color-billing-to-validate)';
    case BillingStatus.VALIDATED:
      return 'var(--color-billing-validated)';
    case BillingStatus.INVOICED:
      return 'var(--color-billing-invoiced)';
    default:
      return 'var(--color-bg-page)';
  }
};

/**
 * oldValue/newValue arrivano dal backend come JSON piatto dei soli campi di fatturazione
 * (ReportBillingService.billingSnapshotJson) - nessun campo annidato da gestire.
 */
type BillingSnapshot = Record<string, string | number | null>;

const SNAPSHOT_FIELD_ORDER = [
  'billingStatus',
  'commercialReference',
  'registeredWorkMinutes',
  'registeredTravelMinutes',
  'registeredKm',
  'billedWorkMinutes',
  'billedTravelMinutes',
  'billedKm',
  'adjustmentReason',
  'notes',
];

const parseSnapshot = (json: string | null): BillingSnapshot | null => {
  if (!json) return null;
  try {
    return JSON.parse(json) as BillingSnapshot;
  } catch {
    return null;
  }
};

const formatSnapshotValue = (field: string, value: string | number | null, t: (key: string) => string): string => {
  if (value === null || value === undefined || value === '') return '—';
  if (field === 'billingStatus') return t(`billing.status.${value}`);
  // Solo i valori FATTURATI passano al formato gg:hh:mm; i registrati restano in ore e minuti
  // perche' devono restare confrontabili col dato della pagina report.
  if (field === 'billedWorkMinutes' || field === 'billedTravelMinutes') return formatMinutesToWorkdayFormat(Number(value));
  if (field.endsWith('Minutes')) return formatMinutesToHoursMinutes(Number(value));
  if (field.endsWith('Km')) return `${value} km`;
  return String(value);
};

/** Righe leggibili dei campi cambiati fra oldValue e newValue, per lo storico. */
const describeSnapshotChanges = (
  oldValue: string | null,
  newValue: string | null,
  t: (key: string) => string
): string[] => {
  const oldSnap = parseSnapshot(oldValue);
  const newSnap = parseSnapshot(newValue);
  if (!newSnap) return [];

  if (!oldSnap) {
    return SNAPSHOT_FIELD_ORDER
      .filter((field) => newSnap[field] !== null && newSnap[field] !== undefined && newSnap[field] !== '')
      .map((field) => `${t(`billing.fieldLabels.${field}`)}: ${formatSnapshotValue(field, newSnap[field], t)}`);
  }

  return SNAPSHOT_FIELD_ORDER
    .filter((field) => oldSnap[field] !== newSnap[field])
    .map(
      (field) =>
        `${t(`billing.fieldLabels.${field}`)}: ${formatSnapshotValue(field, oldSnap[field], t)} → ${formatSnapshotValue(field, newSnap[field], t)}`
    );
};

/** Messaggio comprensibile per gli errori di formato gg:hh:mm (feedback ambiente di test 23/09/2026). */
const timeErrorMessage = (error: 'format' | 'range' | undefined, t: (key: string) => string): string | null => {
  if (!error) return null;
  return error === 'format' ? t('billing.errors.timeFormatInvalid') : t('billing.errors.timeComponentOutOfRange');
};

const ReportBillingPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { showSuccess, showError, NotificationContainer } = useNotification();
  const { confirm, ConfirmDialog } = useConfirm();

  const { report, loading: reportLoading, error: reportError } = useReport(id);
  const { billing, loading: billingLoading, refresh: refreshBilling } = useReportBilling(id);

  const [history, setHistory] = useState<BillingHistoryEvent[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const [commercialReference, setCommercialReference] = useState('');
  const [notes, setNotes] = useState('');
  const [billedWorkTimeInput, setBilledWorkTimeInput] = useState('');
  const [billedTravelTimeInput, setBilledTravelTimeInput] = useState('');
  const [billedWorkTimeError, setBilledWorkTimeError] = useState<string | null>(null);
  const [billedTravelTimeError, setBilledTravelTimeError] = useState<string | null>(null);
  const [billedKm, setBilledKm] = useState<string>('');
  const [adjustmentReason, setAdjustmentReason] = useState('');

  const currentUser = authService.getCurrentUser();
  const isFinance = currentUser?.role === 'FINANCE';

  useEffect(() => {
    if (billing) {
      setCommercialReference(billing.commercialReference || '');
      setNotes(billing.notes || '');
      setBilledWorkTimeInput(formatMinutesToWorkdayFormat(billing.billedWorkMinutes ?? 0));
      setBilledTravelTimeInput(formatMinutesToWorkdayFormat(billing.billedTravelMinutes ?? 0));
      setBilledWorkTimeError(null);
      setBilledTravelTimeError(null);
      setBilledKm(billing.billedKm != null ? String(billing.billedKm) : '');
      setAdjustmentReason(billing.adjustmentReason || '');
    }
  }, [billing]);

  const loadHistory = useCallback(async () => {
    if (!id) return;
    const events = await apiClient.getReportBillingHistory(id);
    setHistory(events);
  }, [id]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  useEffect(() => {
    if (reportError || (!reportLoading && !report)) {
      showError(t('messages.errorLoadingReport'));
      navigate('/');
    }
  }, [report, reportLoading, reportError, showError, t, navigate]);

  const isFormDirty = (): boolean => {
    if (!billing) return false;
    return (
      commercialReference !== (billing.commercialReference || '') ||
      notes !== (billing.notes || '') ||
      adjustmentReason !== (billing.adjustmentReason || '') ||
      billedKm !== (billing.billedKm != null ? String(billing.billedKm) : '') ||
      billedWorkTimeInput !== formatMinutesToWorkdayFormat(billing.billedWorkMinutes ?? 0) ||
      billedTravelTimeInput !== formatMinutesToWorkdayFormat(billing.billedTravelMinutes ?? 0)
    );
  };

  // Salva il form; ritorna true se il salvataggio e' riuscito.
  const saveBilling = async (silent = false): Promise<boolean> => {
    if (!id) return false;

    const workResult = parseWorkdayFormatToMinutes(billedWorkTimeInput);
    const travelResult = parseWorkdayFormatToMinutes(billedTravelTimeInput);
    setBilledWorkTimeError(timeErrorMessage(workResult.error, t));
    setBilledTravelTimeError(timeErrorMessage(travelResult.error, t));
    if (workResult.error || travelResult.error) {
      showError(t('billing.errors.invalidBeforeSave'));
      return false;
    }

    setIsSaving(true);
    try {
      await apiClient.updateReportBilling(id, {
        commercialReference,
        billedWorkMinutes: workResult.minutes,
        billedTravelMinutes: travelResult.minutes,
        billedKm: billedKm !== '' ? parseInt(billedKm, 10) : undefined,
        adjustmentReason,
        notes,
      });
      await refreshBilling();
      await loadHistory();
      if (!silent) showSuccess(t('billing.savedSuccess'));
      return true;
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      showError(`${t('billing.errorSaving')}: ${message}`);
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveBilling = async (): Promise<void> => {
    await saveBilling();
  };

  const handleBillingStatusChange = async (newStatus: BillingStatus): Promise<void> => {
    if (!id || newStatus === billing?.billingStatus) return;

    if (newStatus === BillingStatus.INVOICED) {
      const confirmed = await confirm({
        title: t('billing.confirmInvoice.title'),
        description: t('billing.confirmInvoice.description'),
      });
      if (!confirmed) return;
    }

    // Salva le modifiche pendenti prima del cambio stato: il refresh ricarica il form dal server
    if (isFormDirty() && !(await saveBilling(true))) return;

    try {
      await apiClient.updateReportBillingStatus(id, { status: newStatus });
      await refreshBilling();
      await loadHistory();
      showSuccess(t('billing.statusUpdatedSuccess'));
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      showError(`${t('billing.errorUpdatingStatus')}: ${message}`);
    }
  };

  if (reportLoading || billingLoading || !report) {
    return <div className="loading">{t('app.loading')}</div>;
  }

  // Se si arriva dalla lista fatturazione (home Finance, o /billing per ADMIN/SUPER_ADMIN), il
  // primo passo della breadcrumb deve tornare li' e non alla dashboard standard - altrimenti
  // l'utente salta da un mondo all'altro senza rendersene conto.
  const cameFromBillingList = (location.state as { from?: string } | null)?.from === 'billing-list';
  const homeBreadcrumbItem: BreadcrumbItem = cameFromBillingList
    ? { label: '', translateKey: 'breadcrumb.financeHome', path: isFinance ? '/' : '/billing' }
    : { label: '', translateKey: 'breadcrumb.dashboard', path: getDashboardPath(currentUser) };

  const breadcrumbItems: BreadcrumbItem[] = [
    homeBreadcrumbItem,
    {
      label: `${t('breadcrumb.report')} #${report.number || ''}`,
      path: `/report/${id}`,
      // Propaga la provenienza al report: se non lo facessimo, cliccando questo link la
      // breadcrumb della pagina report ripartirebbe sempre da Dashboard, perdendo il contesto.
      state: cameFromBillingList ? { from: 'billing-list' } : undefined,
    },
    { label: '', translateKey: 'breadcrumb.billingDetail' },
  ];

  if (!billing) {
    // Report non billing-eligible: nessuna pagina (README: "l'API risponde 404"). L'header resta
    // comunque visibile, altrimenti l'utente resta bloccato senza modo di tornare al report.
    return (
      <div className="report-billing-page">
        <NotificationContainer />
        <FormHeader breadcrumbItems={breadcrumbItems} />
        <div className="not-billable">{t('billing.notBillable')}</div>
      </div>
    );
  }

  // SPEC 7 - Due fasi: se billing_status e' null (report non ancora chiuso), solo il riferimento
  // commerciale e le note sono visibili; card dei valori e storico non compaiono.
  const showBillingDetails = billing.billingStatus !== null;
  const readOnly = !billing.editable;

  // Feedback ambiente di test 23/09/2026: il cambio di stato e la modifica dei valori non sono
  // piu' riservati a FINANCE. Chiunque abbia accesso alla pagina (la visibilita' si gestisce a
  // monte, vedi ReportForm.tsx e MainForm.tsx) puo' compiere tutte le operazioni, senza distinzione
  // di ruolo - la pagina e' identica per tutti.

  const billedWorkParsed = parseWorkdayFormatToMinutes(billedWorkTimeInput);
  const billedTravelParsed = parseWorkdayFormatToMinutes(billedTravelTimeInput);

  const workDelta = billedWorkParsed.minutes != null && billing.registeredWorkMinutes != null
    ? billedWorkParsed.minutes - billing.registeredWorkMinutes
    : null;
  const travelDelta = billedTravelParsed.minutes != null && billing.registeredTravelMinutes != null
    ? billedTravelParsed.minutes - billing.registeredTravelMinutes
    : null;
  const kmDelta = billedKm !== '' && billing.registeredKm != null
    ? parseInt(billedKm, 10) - billing.registeredKm
    : null;

  const formatTimeDelta = (delta: number | null): string => {
    if (delta === null || delta === 0) return formatMinutesToWorkdayFormat(0);
    const sign = delta > 0 ? '+' : '-';
    return `${sign}${formatMinutesToWorkdayFormat(Math.abs(delta))}`;
  };

  const formatKmDelta = (delta: number | null): string => {
    if (delta === null || delta === 0) return '0 km';
    const sign = delta > 0 ? '+' : '-';
    return `${sign}${Math.abs(delta)} km`;
  };

  const notesCreatedLine = billing.notesCreatedAt
    ? t('billing.notesCreatedBy', {
        user: billing.notesCreatedBy || '',
        date: safeFormatDate(billing.notesCreatedAt, 'dd/MM/yyyy HH:mm'),
      })
    : null;
  const notesUpdatedLine = billing.notesUpdatedAt
    ? t('billing.notesUpdatedBy', {
        user: billing.notesUpdatedBy || '',
        date: safeFormatDate(billing.notesUpdatedAt, 'dd/MM/yyyy HH:mm'),
      })
    : null;
  const notesWasModifiedAfterCreation = billing.notesCreatedAt !== billing.notesUpdatedAt;
  const notesTrackingLine = notesCreatedLine
    ? (notesWasModifiedAfterCreation && notesUpdatedLine ? `${notesCreatedLine} · ${notesUpdatedLine}` : notesCreatedLine)
    : null;

  const statusChangeWidget = (
    <section className="form-section sidebar-section billing-status-widget">
      <h3>{t('billing.statusSectionTitle')}</h3>
      <select
        aria-label={t('billing.statusSectionTitle')}
        value={billing.billingStatus || ''}
        onChange={(e) => handleBillingStatusChange(e.target.value as BillingStatus)}
        disabled={readOnly || billing.billingStatus === BillingStatus.INVOICED}
        className="status-select"
        style={{
          backgroundColor: getBillingStatusColorVar(billing.billingStatus),
          color: '#000',
          fontWeight: 'bold',
          padding: '8px',
          border: '2px solid #ccc',
          borderRadius: '4px',
        }}
      >
        {validBillingStatusTransitions(billing.billingStatus).map((status) => (
          <option key={status} value={status}>
            {t(`billing.status.${status}`)}
          </option>
        ))}
      </select>
    </section>
  );

  return (
    <div className="report-billing-page">
      <NotificationContainer />
      <ConfirmDialog />
      <FormHeader breadcrumbItems={breadcrumbItems}>
        <div className="header-actions">
          <button
            onClick={handleSaveBilling}
            className={`btn-save ${isSaving ? 'btn-loading' : ''}`}
            disabled={isSaving || readOnly}
          >
            {isSaving ? (
              <><span className="btn-spinner"></span> {t('common.saving')}</>
            ) : (
              <><span className="material-icons">save</span> {t('common.save')}</>
            )}
          </button>
        </div>
      </FormHeader>

      <div className="form-layout">
        <div className="form-main-content">
          <section className="form-section sidebar-section">
            <h3>{t('billing.commercialReference')}</h3>
            <div className="form-field">
              <input
                type="text"
                value={commercialReference}
                onChange={(e) => setCommercialReference(e.target.value)}
                disabled={readOnly}
                className={readOnly ? 'input-readonly' : ''}
                aria-label={t('billing.commercialReference')}
              />
            </div>
            <div className="form-field">
              <label>{t('billing.notes')}</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={readOnly}
                className={readOnly ? 'input-readonly' : ''}
                rows={2}
                aria-label={t('billing.notes')}
              />
              {notesTrackingLine && <div className="notes-tracking">{notesTrackingLine}</div>}
            </div>
          </section>

          {showBillingDetails && (
            <>
              <section className="form-section sidebar-section">
                <h3>{t('billing.billedValues')}</h3>
                <table className="billing-values-table">
                  <thead>
                    <tr>
                      <th>{t('billing.item')}</th>
                      <th>{t('billing.agreed')}</th>
                      <th>{t('billing.registered')}</th>
                      <th>{t('billing.billed')}</th>
                      <th>{t('billing.delta')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{t('billing.workHours')}</td>
                      <td>{formatMinutesToHoursMinutes((report.workTime ?? 0) * 60)}</td>
                      <td>{formatMinutesToHoursMinutes(billing.registeredWorkMinutes || 0)}</td>
                      <td>
                        <input
                          type="text"
                          value={billedWorkTimeInput}
                          onChange={(e) => { setBilledWorkTimeInput(e.target.value); setBilledWorkTimeError(null); }}
                          onBlur={() => setBilledWorkTimeError(timeErrorMessage(parseWorkdayFormatToMinutes(billedWorkTimeInput).error, t))}
                          disabled={readOnly}
                          className={readOnly ? 'input-readonly' : ''}
                          aria-label={t('billing.workHours')}
                          placeholder="0:00:00"
                        />
                        {billedWorkTimeError && <div className="field-error-text">{billedWorkTimeError}</div>}
                      </td>
                      <td>{formatTimeDelta(workDelta)}</td>
                    </tr>
                    <tr>
                      <td>{t('billing.travelHours')}</td>
                      <td>{formatMinutesToHoursMinutes((report.travelTime ?? 0) * 60)}</td>
                      <td>{formatMinutesToHoursMinutes(billing.registeredTravelMinutes || 0)}</td>
                      <td>
                        <input
                          type="text"
                          value={billedTravelTimeInput}
                          onChange={(e) => { setBilledTravelTimeInput(e.target.value); setBilledTravelTimeError(null); }}
                          onBlur={() => setBilledTravelTimeError(timeErrorMessage(parseWorkdayFormatToMinutes(billedTravelTimeInput).error, t))}
                          disabled={readOnly}
                          className={readOnly ? 'input-readonly' : ''}
                          aria-label={t('billing.travelHours')}
                          placeholder="0:00:00"
                        />
                        {billedTravelTimeError && <div className="field-error-text">{billedTravelTimeError}</div>}
                      </td>
                      <td>{formatTimeDelta(travelDelta)}</td>
                    </tr>
                    <tr>
                      <td>{t('billing.km')}</td>
                      <td>{report.travelKm ?? 0} km</td>
                      <td>{billing.registeredKm ?? 0} km</td>
                      <td>
                        <input
                          type="number"
                          value={billedKm}
                          onChange={(e) => setBilledKm(e.target.value)}
                          disabled={readOnly}
                          className={readOnly ? 'input-readonly' : ''}
                          aria-label={t('billing.km')}
                        />
                      </td>
                      <td>{formatKmDelta(kmDelta)}</td>
                    </tr>
                  </tbody>
                </table>
                <div className="form-field">
                  <label>{t('billing.adjustmentReason')}</label>
                  <textarea
                    value={adjustmentReason}
                    onChange={(e) => setAdjustmentReason(e.target.value)}
                    disabled={readOnly}
                    className={readOnly ? 'input-readonly' : ''}
                    rows={2}
                  />
                </div>
              </section>

              <section className="form-section sidebar-section">
                <h3>{t('billing.history')}</h3>
                {history.length === 0 ? (
                  <div className="no-results">{t('billing.noHistory')}</div>
                ) : (
                  <table className="billing-history-table">
                    <thead>
                      <tr>
                        <th>{t('billing.historyOperation')}</th>
                        <th>{t('billing.historyChanges')}</th>
                        <th>{t('billing.historyUser')}</th>
                        <th>{t('billing.historyDate')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map((event) => {
                        const changes = describeSnapshotChanges(event.oldValue, event.newValue, t);
                        return (
                          <tr key={event.id}>
                            <td data-label={t('billing.historyOperation')}>{t(`billing.operation.${event.operation}`)}</td>
                            <td data-label={t('billing.historyChanges')}>
                              {changes.length === 0 ? '—' : (
                                <ul className="billing-history-changes">
                                  {changes.map((line, idx) => <li key={idx}>{line}</li>)}
                                </ul>
                              )}
                            </td>
                            <td data-label={t('billing.historyUser')}>{event.userEmail || '-'}</td>
                            <td data-label={t('billing.historyDate')}>{safeFormatDate(event.occurredAt, 'dd/MM/yyyy HH:mm')}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </section>
            </>
          )}
        </div>

        <aside className="form-sidebar">
          <ReportStatusInfo report={report} />
          {showBillingDetails && statusChangeWidget}
          <ReportInfo report={report} />
        </aside>
      </div>
    </div>
  );
};

export default ReportBillingPage;
